import React, { useState } from 'react';
import { User, DownloadCloud, Plus, Trash2, BatteryCharging, CheckCircle, XCircle } from 'lucide-react';
import PageHeader from '../../components/PageHeader/PageHeader';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import './SolarAssessment.css';

export default function SolarAssessment() {
  // Helper to get the correct local date by adjusting for timezone offset
  const getLocalDate = () => {
    const now = new Date();
    now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
    return now.toISOString().split('T')[0];
  };

  const [isGenerating, setIsGenerating] = useState(false);
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });
  
  const initialFormData = {
    date: getLocalDate(),
    clientName: '',
    clientPhone: '',
    clientEmail: '',
    siteAddress: ''
  };

  const initialAppliances = [
    { id: Date.now(), name: '', rating: '', qty: 1, totalPower: 0, hours: '', consumption: 0 }
  ];

  const [formData, setFormData] = useState(initialFormData);
  const [appliances, setAppliances] = useState(initialAppliances);

  // Custom Toast Notification trigger
  const showToast = (message, type = 'success') => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: '', type: 'success' });
    }, 5000);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleApplianceChange = (id, field, value) => {
    let val = value;

    // Strict validation: Hours cannot exceed 24
    if (field === 'hours') {
      if (val !== '' && parseFloat(val) > 24) {
        val = '24';
      }
      if (val !== '' && parseFloat(val) < 0) {
        val = '0';
      }
    }

    const updatedAppliances = appliances.map(app => {
      if (app.id === id) {
        const updatedApp = { ...app, [field]: val };
        
        const q = parseFloat(updatedApp.qty) || 0;
        const r = parseFloat(updatedApp.rating) || 0;
        const h = parseFloat(updatedApp.hours) || 0;
        
        updatedApp.totalPower = q * r;
        updatedApp.consumption = (updatedApp.totalPower * h) / 1000;
        
        return updatedApp;
      }
      return app;
    });
    setAppliances(updatedAppliances);
  };

  const addApplianceRow = () => {
    setAppliances([
      ...appliances,
      { id: Date.now(), name: '', rating: '', qty: 1, totalPower: 0, hours: '', consumption: 0 }
    ]);
  };

  const removeApplianceRow = (id) => {
    if (appliances.length > 1) {
      setAppliances(appliances.filter(app => app.id !== id));
    }
  };

  const getGrandTotalKWh = () => {
    return appliances.reduce((sum, app) => sum + app.consumption, 0);
  };

  const handleGeneratePDFAndSend = async (e) => {
    e.preventDefault();
    setIsGenerating(true);

    try {
      const formElement = document.getElementById('pdf-content-area');
      formElement.classList.add('pdf-mode');

      const canvas = await html2canvas(formElement, { scale: 2, useCORS: true });
      const imgData = canvas.toDataURL('image/png');
      
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      
      // Calculate how tall the image is proportionally in the PDF
      const imgHeight = (canvas.height * pdfWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;

      // Render the first page
      pdf.addImage(imgData, 'PNG', 0, position, pdfWidth, imgHeight);
      heightLeft -= pageHeight;

      // Loop to render subsequent pages if the table is long
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight; // Shift the image up to show the next slice
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, pdfWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      formElement.classList.remove('pdf-mode');

      const pdfBase64 = pdf.output('datauristring');

      const response = await fetch('https://singularitytech.co.za/send_email.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          clientName: formData.clientName,
          clientEmail: formData.clientEmail,
          clientPhone: formData.clientPhone,
          pdfBase64: pdfBase64
        })
      });

      const result = await response.json();

      if (result.status === 'success') {
        showToast("Quote request successfully sent", "success");
        // Clear the form back to defaults
        setFormData(initialFormData);
        setAppliances([{ id: Date.now(), name: '', rating: '', qty: 1, totalPower: 0, hours: '', consumption: 0 }]);
      } else {
        showToast("Server error: Could not dispatch the automated email.", "error");
      }

    } catch (error) {
      console.error("Error generating or sending PDF:", error);
      showToast("There was a critical error processing the document.", "error");
      document.getElementById('pdf-content-area').classList.remove('pdf-mode');
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="assessment-page">
      <PageHeader 
        title={<>Load <span>Profile Assessment</span></>} 
        description="Complete the appliance and energy calculation table below. The form will be processed into an official PDF document and securely sent to our team for a quote."
      />

      {/* Progress Loader Overlay */}
      {isGenerating && (
        <div className="loader-overlay">
          <div className="loader-spinner"></div>
          <h2>Processing Document</h2>
          <p>Please wait while we generate your PDF and securely dispatch the email...</p>
        </div>
      )}

      {/* Custom Toast Notification */}
      {notification.show && (
        <div className={`toast-notification ${notification.type}`}>
          {notification.type === 'success' ? <CheckCircle size={24} /> : <XCircle size={24} />}
          <span>{notification.message}</span>
        </div>
      )}

      <div className="assessment-main-content">
        <div className="container">
          
          <form className="assessment-form-card" onSubmit={handleGeneratePDFAndSend}>
            
            <div id="pdf-content-area" className="pdf-printable-area">
              
              <div className="form-document-header">
                <h2>Singularitytech</h2>
                <p>Solar System and Backup System Site Assessment and Risk Form</p>
              </div>

              <div className="form-section">
                <h3>1. Client Details</h3>
                <div className="form-grid">
                  <div className="form-group">
                    <label>Client Name</label>
                    <input type="text" name="clientName" value={formData.clientName} onChange={handleInputChange} required />
                  </div>
                  <div className="form-group">
                    <label>Client Address</label>
                    <input type="text" name="siteAddress" value={formData.siteAddress} onChange={handleInputChange} required />
                  </div>
                  <div className="form-group">
                    <label>Contact Number</label>
                    <input type="tel" name="clientPhone" value={formData.clientPhone} onChange={handleInputChange} required />
                  </div>
                  <div className="form-group">
                    <label>Email Address</label>
                    <input type="email" name="clientEmail" value={formData.clientEmail} onChange={handleInputChange} required />
                  </div>
                  <div className="form-group">
                    <label>Date of Assessment</label>
                    <input type="date" name="date" value={formData.date} onChange={handleInputChange} required />
                  </div>
                </div>
              </div>

              <div className="form-section assessor-section">
                <div className="form-grid">
                  <div className="form-group">
                    <label>Assessor Name</label>
                    <input type="text" value="Matlakeng Tlou" readOnly className="read-only-input" />
                  </div>
                  <div className="form-group">
                    <label>Assessor Contact</label>
                    <input type="text" value="+27 66 376 9466" readOnly className="read-only-input" />
                  </div>
                </div>
              </div>

              <div className="form-section">
                <h3>2. Solar System Sizing Assessment</h3>
                <h4 style={{ color: 'var(--brand-green)', marginBottom: '15px' }}>2.1. Current Energy Consumption</h4>
                <p style={{ marginBottom: '10px', fontSize: '14px', fontWeight: 'bold' }}>Appliance Power and Energy Calculation Table</p>
                
                <div className="appliance-table-wrapper">
                  <table className="appliance-table">
                    <thead>
                      <tr>
                        <th>Appliance Name</th>
                        <th className="col-sm">Rating (W)</th>
                        <th className="col-xs">Qty</th>
                        <th className="col-md">Total Power (W)</th>
                        <th className="col-md">Average Daily Usage (hrs)</th>
                        <th className="col-md">Consumption/Day (kWh)</th>
                        <th className="col-xs hide-in-pdf"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {appliances.map((app) => (
                        <tr key={app.id}>
                          <td>
                            <input type="text" value={app.name} onChange={(e) => handleApplianceChange(app.id, 'name', e.target.value)} required />
                          </td>
                          <td>
                            <input type="number" min="0" value={app.rating} onChange={(e) => handleApplianceChange(app.id, 'rating', e.target.value)} required />
                          </td>
                          <td>
                            <input type="number" min="1" value={app.qty} onChange={(e) => handleApplianceChange(app.id, 'qty', e.target.value)} required />
                          </td>
                          <td className="total-cell">{app.totalPower}</td>
                          <td>
                            <input 
                              type="number" 
                              min="0" 
                              max="24"
                              step="0.1" 
                              value={app.hours} 
                              onChange={(e) => handleApplianceChange(app.id, 'hours', e.target.value)} 
                              required 
                            />
                          </td>
                          <td className="total-cell">{app.consumption.toFixed(2)}</td>
                          <td className="hide-in-pdf action-cell">
                            {appliances.length > 1 && (
                              <button type="button" className="btn-remove" onClick={() => removeApplianceRow(app.id)}><Trash2 size={16} /></button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  
                  <div className="table-footer hide-in-pdf">
                    <button type="button" className="btn-add-row" onClick={addApplianceRow}>
                      <Plus size={16} /> Add Appliance
                    </button>
                  </div>
                  
                  <div className="grand-total-row">
                    <span>Total Daily Consumption:</span>
                    <strong>{getGrandTotalKWh().toFixed(2)} kWh</strong>
                  </div>
                </div>
              </div>
              
              <div className="form-document-footer">
                <p>Singularitytech (PTY) LTD | Infrastructure & Innovation</p>
                <p>www.singularitytech.co.za</p>
              </div>

            </div>
            
            <div className="form-actions">
              <button type="submit" className="btn-primary generate-btn" disabled={isGenerating}>
                {isGenerating ? 'Processing Document...' : <><DownloadCloud size={20}/> Get Quote</>}
              </button>
            </div>

          </form>

        </div>
      </div>
    </div>
  );
}